import React from 'react';
import { Badge, Text, VStack, HStack, Avatar, Tooltip, IconButton } from '@chakra-ui/react';
import { FiCopy } from 'react-icons/fi';
import ContactTypeBadges from './ContactTypeBadges';
import ContactScoreCard from './Scoring/ContactScoreCard';
import TagsCell from './TagsCell';
import moment from 'moment';

export const ALL_CONTACTS_COLUMNS = [
  {
    id: 'name',
    label: 'Name',
    accessor: (contact) => `${contact.firstName} ${contact.lastName}`,
    sortField: 'firstName', // Firestore field for sorting
    sortable: true,
    filterable: true,
    filterType: 'text',
    width: '250px',
    isDefault: true,
    renderCell: (contact, onClick) => (
      <HStack spacing={3} cursor="pointer" onClick={() => onClick && onClick(contact)}>
        <Avatar
          size="sm"
          name={`${contact.firstName} ${contact.lastName}`}
          bg={contact.isFavorite ? 'yellow.400' : 'blue.500'}
        />
        <VStack align="flex-start" spacing={0}>
          <Text fontWeight="semibold" fontSize="sm">
            {contact.firstName} {contact.middleInitial && `${contact.middleInitial}. `}{contact.lastName}
          </Text>
          {contact.jobTitle && (
            <Text fontSize="xs" color="gray.500">
              {contact.jobTitle}
            </Text>
          )}
        </VStack>
      </HStack>
    ),
  },
  {
    id: 'types',
    label: 'Types',
    accessor: (contact) => (contact.contactTypes || []).join(', '),
    sortable: false,
    filterable: true,
    filterType: 'multiselect',
    filterOptions: [], // Dynamically injected from CrmSettingsContext
    width: '200px',
    isDefault: true,
    renderCell: (contact) => (
      <ContactTypeBadges
        contactTypes={contact.contactTypes || []}
        primaryContactType={contact.primaryContactType}
        size="xs"
        maxDisplay={2}
      />
    ),
  },
  {
    id: 'email',
    label: 'Email',
    accessor: (contact) => contact.email || '',
    sortField: 'email', // Firestore field for sorting
    sortable: true,
    filterable: true,
    filterType: 'text',
    width: '220px',
    isDefault: true,
    renderCell: (contact, onClick, onCopyEmail) => (
      contact.email ? (
        <HStack spacing={2}>
          <Text fontSize="sm" noOfLines={1} maxW="180px">
            {contact.email}
          </Text>
          <IconButton
            icon={<FiCopy />}
            size="xs"
            variant="ghost"
            aria-label="Copy email"
            onClick={(e) => {
              e.stopPropagation();
              if (onCopyEmail) onCopyEmail(contact.email);
            }}
          />
        </HStack>
      ) : (
        <Text fontSize="sm" color="gray.400">—</Text>
      )
    ),
  },
  {
    id: 'phone',
    label: 'Phone',
    accessor: (contact) => contact.phone || '',
    sortField: 'phone', // Firestore field for sorting
    sortable: true,
    filterable: true,
    filterType: 'text',
    width: '150px',
    isDefault: true,
    renderCell: (contact, onClick, onCopyEmail, onCopyPhone) => (
      contact.phone ? (
        <HStack spacing={2}>
          <Text fontSize="sm">
            {contact.phone}
          </Text>
          <IconButton
            icon={<FiCopy />}
            size="xs"
            variant="ghost"
            aria-label="Copy phone"
            onClick={(e) => {
              e.stopPropagation();
              if (onCopyPhone) onCopyPhone(contact.phone);
            }}
          />
        </HStack>
      ) : (
        <Text fontSize="sm" color="gray.400">—</Text>
      )
    ),
  },
  {
    id: 'company',
    label: 'Company',
    accessor: (contact) => contact.company || '',
    sortField: 'company', // Firestore field for sorting
    sortable: true,
    filterable: true,
    filterType: 'text',
    width: '180px',
    isDefault: false,
    renderCell: (contact) => (
      <Text fontSize="sm" noOfLines={1}>
        {contact.company || '—'}
      </Text>
    ),
  },
  {
    id: 'tags',
    label: 'Tags',
    accessor: (contact) => (contact.tags || []).join(', '),
    sortable: false,
    filterable: true,
    filterType: 'multiselect',
    filterOptions: [], // Dynamically injected from CrmSettingsContext + contact data
    width: '150px',
    isDefault: true,
    renderCell: (contact) => <TagsCell tags={contact.tags} />,
  },
  {
    id: 'totalDeals',
    label: 'Deals',
    accessor: (contact) => contact.stats?.totalDeals || 0,
    sortField: 'stats.totalDeals', // Firestore field for sorting
    sortable: true,
    filterable: false,
    width: '100px',
    isDefault: false,
    renderCell: (contact) => (
      <Tooltip 
        label={`${contact.stats?.totalDealsAsBorrower || 0} as borrower, ${contact.stats?.totalDealsAsRelatedParty || 0} as party`}
        hasArrow
      >
        <Badge colorScheme="blue" fontSize="sm">
          {contact.stats?.totalDeals || 0}
        </Badge>
      </Tooltip>
    ),
  },
  {
    id: 'lastContact',
    label: 'Last Contact',
    accessor: (contact) => {
      const timestamp = contact.stats?.lastContactDate;
      if (!timestamp) return 0;
      return timestamp.seconds || new Date(timestamp).getTime() / 1000;
    },
    sortField: 'stats.lastContactDate', // Firestore field for sorting
    sortable: true,
    filterable: false,
    width: '140px',
    isDefault: true,
    renderCell: (contact) => {
      const formatLastContact = (timestamp) => {
        if (!timestamp) return 'Never';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return moment(date).fromNow();
      };

      return (
        <Text fontSize="sm" color="gray.600">
          {formatLastContact(contact.stats?.lastContactDate)}
        </Text>
      );
    },
  },
  {
    id: 'source',
    label: 'Source',
    accessor: (contact) => contact.source || '',
    sortField: 'source', // Firestore field for sorting
    sortable: true,
    filterable: true,
    filterType: 'text',
    width: '120px',
    isDefault: true,
    renderCell: (contact) => (
      <Text fontSize="sm" textTransform="capitalize">
        {contact.source || '—'}
      </Text>
    ),
  },
  {
    id: 'secondaryEmail',
    label: 'Secondary Email',
    accessor: (contact) => contact.secondaryEmail || '',
    sortField: 'secondaryEmail', // Firestore field for sorting
    sortable: true,
    filterable: true,
    filterType: 'text',
    width: '220px',
    isDefault: false,
    renderCell: (contact) => (
      <Text fontSize="sm" noOfLines={1}>
        {contact.secondaryEmail || '—'}
      </Text>
    ),
  },
  {
    id: 'secondaryPhone',
    label: 'Secondary Phone',
    accessor: (contact) => contact.secondaryPhone || '',
    sortField: 'secondaryPhone', // Firestore field for sorting
    sortable: true,
    filterable: true,
    filterType: 'text',
    width: '150px',
    isDefault: false,
    renderCell: (contact) => (
      <Text fontSize="sm">
        {contact.secondaryPhone || '—'}
      </Text>
    ),
  },
  {
    id: 'licenseNumber',
    label: 'License #',
    accessor: (contact) => contact.licenseNumber || '',
    sortField: 'licenseNumber', // Firestore field for sorting
    sortable: true,
    filterable: true,
    filterType: 'text',
    width: '130px',
    isDefault: false,
    renderCell: (contact) => (
      <Text fontSize="sm">
        {contact.licenseNumber || '—'}
      </Text>
    ),
  },
  {
    id: 'licenseState',
    label: 'License State',
    accessor: (contact) => contact.licenseState || '',
    sortField: 'licenseState', // Firestore field for sorting
    sortable: true,
    filterable: true,
    filterType: 'text',
    width: '120px',
    isDefault: false,
    renderCell: (contact) => (
      <Text fontSize="sm">
        {contact.licenseState || '—'}
      </Text>
    ),
  },
  {
    id: 'wonDeals',
    label: 'Won Deals',
    accessor: (contact) => contact.stats?.wonDeals || 0,
    sortField: 'stats.wonDeals', // Firestore field for sorting
    sortable: true,
    filterable: false,
    width: '100px',
    isDefault: false,
    renderCell: (contact) => (
      <Badge colorScheme="green" fontSize="sm">
        {contact.stats?.wonDeals || 0}
      </Badge>
    ),
  },
  {
    id: 'lifetimeValue',
    label: 'Lifetime Value',
    accessor: (contact) => contact.stats?.lifetimeValue || 0,
    sortField: 'stats.lifetimeValue', // Firestore field for sorting
    sortable: true,
    filterable: false,
    width: '140px',
    isDefault: false,
    renderCell: (contact) => (
      <Text fontSize="sm" fontWeight="semibold">
        {contact.stats?.lifetimeValue > 0 
          ? `$${(contact.stats.lifetimeValue / 1000).toFixed(0)}K`
          : '—'}
      </Text>
    ),
  },
  {
    id: 'isActive',
    label: 'Active',
    accessor: (contact) => contact.isActive ? 'Yes' : 'No',
    sortField: 'isActive', // Firestore field for sorting
    sortable: true,
    filterable: true,
    filterType: 'multiselect',
    filterOptions: [
      { value: 'Yes', label: 'Active' },
      { value: 'No', label: 'Inactive' },
    ],
    width: '100px',
    isDefault: false,
    renderCell: (contact) => (
      <Badge colorScheme={contact.isActive ? 'green' : 'gray'} fontSize="xs">
        {contact.isActive ? 'Active' : 'Inactive'}
      </Badge>
    ),
  },
  {
    id: 'createdAt',
    label: 'Created',
    accessor: (contact) => contact.createdAt?.seconds || 0,
    sortField: 'createdAt', // Firestore field for sorting
    sortable: true,
    filterable: false,
    width: '110px',
    isDefault: true,
    renderCell: (contact) => (
      <Text fontSize="sm" color="gray.600">
        {contact.createdAt
          ? moment(contact.createdAt.seconds ? contact.createdAt.seconds * 1000 : contact.createdAt).format('MM/DD/YY')
          : '—'}
      </Text>
    ),
  },
  {
    id: 'score',
    label: 'Score',
    accessor: (contact) => contact.score || 0,
    sortField: 'score', // Firestore field for sorting
    sortable: true,
    filterable: false,
    width: '100px',
    isDefault: false,
    renderCell: (contact) => (
      <ContactScoreCard contact={contact} size="sm" showBreakdown={true} />
    ),
  },
  {
    id: 'owner',
    label: 'Owner',
    accessor: (contact) => contact.ownerId || '',
    sortField: 'owner_id',
    sortable: true,
    filterable: false,
    width: '150px',
    isDefault: false,
    renderCell: (contact, onClick, onCopyEmail, onCopyPhone, usersMap) => {
      const ownerName = usersMap?.get(contact.ownerId) || 'Unassigned';
      return <Text fontSize="sm">{ownerName}</Text>;
    },
  },
  {
    id: 'recordScope',
    label: 'Scope',
    accessor: (contact) => contact.recordScope || 'user',
    sortField: 'record_scope',
    sortable: true,
    filterable: true,
    filterType: 'select',
    width: '110px',
    isDefault: false,
    // Record scope is an admin-only concept: LOs seeing "company record"
    // assume the company is claiming their book. Hidden from non-admin
    // pickers, table renders, and exports.
    adminOnly: true,
    renderCell: (contact) =>
      contact.recordScope === 'company' ? (
        <Badge colorScheme="purple" fontSize="xs">Company</Badge>
      ) : (
        <Text fontSize="sm" color="gray.400">—</Text>
      ),
  },
  // RETR Recruiting Intelligence columns (hidden by default, available via column picker)
  {
    id: 'retrLoanCount',
    label: 'RETR Loan Ct',
    accessor: (contact) => contact.retrLoanCount || 0,
    sortField: 'retrLoanCount',
    sortable: true,
    filterable: true,
    filterType: 'number',
    width: '120px',
    isDefault: false,
    renderCell: (contact) => (
      <Text fontSize="sm" fontWeight="medium">
        {contact.retrLoanCount ? contact.retrLoanCount.toLocaleString() : '—'}
      </Text>
    ),
  },
  {
    id: 'retrLoanVolume',
    label: 'RETR Volume',
    accessor: (contact) => contact.retrLoanVolume || 0,
    sortField: 'retrLoanVolume',
    sortable: true,
    filterable: true,
    filterType: 'number',
    width: '140px',
    isDefault: false,
    renderCell: (contact) => (
      <Text fontSize="sm" fontWeight="medium">
        {contact.retrLoanVolume
          ? `$${(contact.retrLoanVolume / 1000000).toFixed(1)}M`
          : '—'}
      </Text>
    ),
  },
  {
    id: 'retrTotalLoanCount',
    label: 'RETR Total Ct',
    accessor: (contact) => contact.retrTotalLoanCount || 0,
    sortField: 'retrTotalLoanCount',
    sortable: true,
    filterable: true,
    filterType: 'number',
    width: '130px',
    isDefault: false,
    renderCell: (contact) => (
      <Text fontSize="sm" fontWeight="medium">
        {contact.retrTotalLoanCount ? contact.retrTotalLoanCount.toLocaleString() : '—'}
      </Text>
    ),
  },
  {
    id: 'retrTotalLoanVolume',
    label: 'RETR Total Vol',
    accessor: (contact) => contact.retrTotalLoanVolume || 0,
    sortField: 'retrTotalLoanVolume',
    sortable: true,
    filterable: true,
    filterType: 'number',
    width: '140px',
    isDefault: false,
    renderCell: (contact) => (
      <Text fontSize="sm" fontWeight="medium">
        {contact.retrTotalLoanVolume
          ? `$${(contact.retrTotalLoanVolume / 1000000).toFixed(1)}M`
          : '—'}
      </Text>
    ),
  },
  {
    id: 'retrAvgLoan',
    label: 'RETR Avg Loan',
    accessor: (contact) => contact.retrAvgLoan || 0,
    sortField: 'retrAvgLoan',
    sortable: true,
    filterable: true,
    filterType: 'number',
    width: '130px',
    isDefault: false,
    renderCell: (contact) => (
      <Text fontSize="sm" fontWeight="medium">
        {contact.retrAvgLoan
          ? `$${(contact.retrAvgLoan / 1000).toFixed(0)}K`
          : '—'}
      </Text>
    ),
  },
  {
    id: 'retrLoType',
    label: 'RETR Type',
    accessor: (contact) => contact.retrLoType || '',
    sortField: 'retrLoType',
    sortable: true,
    filterable: true,
    filterType: 'text',
    width: '120px',
    isDefault: false,
    renderCell: (contact) => (
      contact.retrLoType ? (
        <Badge
          colorScheme={
            contact.retrLoType === 'Broker' ? 'purple' :
            contact.retrLoType === 'Bank/Cu' ? 'blue' : 'teal'
          }
          fontSize="xs"
        >
          {contact.retrLoType}
        </Badge>
      ) : (
        <Text fontSize="sm" color="gray.400">—</Text>
      )
    ),
  },
  {
    id: 'retrNmlsId',
    label: 'NMLS ID',
    accessor: (contact) => contact.retrNmlsId || '',
    sortField: 'retrNmlsId',
    sortable: true,
    filterable: true,
    filterType: 'text',
    width: '120px',
    isDefault: false,
    renderCell: (contact) => (
      contact.retrNmlsId ? (
        <Badge colorScheme="gray" fontSize="xs" fontFamily="mono">#{contact.retrNmlsId}</Badge>
      ) : (
        <Text fontSize="sm" color="gray.400">—</Text>
      )
    ),
  },
  {
    id: 'retrYearsExperience',
    label: 'Years Exp',
    accessor: (contact) => contact.retrYearsExperience || 0,
    sortField: 'retrYearsExperience',
    sortable: true,
    filterable: true,
    filterType: 'number',
    width: '100px',
    isDefault: false,
    renderCell: (contact) => (
      <Text fontSize="sm" fontWeight="medium">
        {contact.retrYearsExperience != null ? `${contact.retrYearsExperience} yrs` : '—'}
      </Text>
    ),
  },
  {
    id: 'retrBuyerAgentsCount',
    label: 'Buyer Agents',
    accessor: (contact) => contact.retrBuyerAgentsCount || 0,
    sortField: 'retrBuyerAgentsCount',
    sortable: true,
    filterable: true,
    filterType: 'number',
    width: '120px',
    isDefault: false,
    renderCell: (contact) => (
      <Text fontSize="sm" fontWeight="medium">
        {contact.retrBuyerAgentsCount != null ? contact.retrBuyerAgentsCount.toLocaleString() : '—'}
      </Text>
    ),
  },
  {
    id: 'retrCompanyName',
    label: 'RETR Company',
    accessor: (contact) => contact.retrCompanyName || '',
    sortField: 'retrCompanyName',
    sortable: true,
    filterable: true,
    filterType: 'text',
    width: '180px',
    isDefault: false,
    renderCell: (contact) => (
      <Text fontSize="sm" noOfLines={1}>
        {contact.retrCompanyName || '—'}
      </Text>
    ),
  },
  {
    id: 'retrState',
    label: 'RETR State',
    accessor: (contact) => contact.retrState || '',
    sortField: 'retrState',
    sortable: true,
    filterable: true,
    filterType: 'text',
    width: '100px',
    isDefault: false,
    renderCell: (contact) => (
      <Text fontSize="sm">
        {contact.retrState || '—'}
      </Text>
    ),
  },
];

// Default columns in specific display order
export const DEFAULT_VISIBLE_CONTACTS_COLUMNS = [
  'name',
  'source', 
  'phone',
  'email',
  'types',
  'tags',
  'lastContact',
  'createdAt',
];

export const DEFAULT_CONTACTS_PREFERENCES = {
  visibleColumns: DEFAULT_VISIBLE_CONTACTS_COLUMNS,
  columnOrder: DEFAULT_VISIBLE_CONTACTS_COLUMNS,
  sortBy: 'createdAt', // Use createdAt which has automatic indexing with ownerId
  sortDirection: 'desc',
  filters: {},
};

export const getContactColumnById = (columnId) => {
  return ALL_CONTACTS_COLUMNS.find(col => col.id === columnId);
};

export const getVisibleContactsColumns = (visibleColumnIds) => {
  return visibleColumnIds
    .map(id => ALL_CONTACTS_COLUMNS.find(col => col.id === id))
    .filter(Boolean);
};

/**
 * Field definitions for FilterBuilder component
 * Maps column definitions to filter-compatible format
 */
export const CONTACT_FILTER_FIELDS = [
  { id: 'name', label: 'Name', type: 'text', dbField: 'first_name' },
  { id: 'email', label: 'Email', type: 'text', dbField: 'email' },
  { id: 'phone', label: 'Phone', type: 'text', dbField: 'phone' },
  { id: 'company', label: 'Company', type: 'text', dbField: 'company' },
  { id: 'contactTypes', label: 'Contact Type', type: 'array', dbField: 'contact_types', options: [] }, // populated dynamically from CrmSettingsContext
  { id: 'tags', label: 'Tags', type: 'array', dbField: 'tags', options: [] }, // Options populated dynamically
  { id: 'activeCampaigns', label: 'Enrolled in Campaign', type: 'array', dbField: 'active_campaign_ids', options: [] }, // Options from useCampaignFilterOptions
  { id: 'source', label: 'Source', type: 'text', dbField: 'source' },
  { id: 'isActive', label: 'Active', type: 'boolean', dbField: 'is_active' },
  { id: 'isFavorite', label: 'Favorite', type: 'boolean', dbField: 'is_favorite' },
  { id: 'dnc', label: 'DNC: Everything', type: 'boolean', dbField: 'do_not_contact' },
  { id: 'dncCall', label: 'DNC: Call', type: 'boolean', dbField: 'dnc_call' },
  { id: 'dncEmail', label: 'DNC: Email', type: 'boolean', dbField: 'dnc_email' },
  { id: 'dncSms', label: 'DNC: SMS', type: 'boolean', dbField: 'dnc_sms' },
  { id: 'totalDeals', label: 'Total Deals', type: 'number', dbField: 'total_deals' },
  { id: 'lifetimeValue', label: 'Lifetime Value', type: 'number', dbField: 'lifetime_value' },
  { id: 'createdAt', label: 'Created Date', type: 'date', dbField: 'created_at' },
  { id: 'mailingState', label: 'Mailing State', type: 'text', dbField: 'mailing_state' },
  { id: 'mailingCity', label: 'Mailing City', type: 'text', dbField: 'mailing_city' },
  { id: 'licenseState', label: 'License State', type: 'text', dbField: 'license_state' },
  { id: 'ownerId', label: 'Owner', type: 'text', dbField: 'owner_id' },
  { id: 'recordScope', label: 'Record Scope', type: 'select', dbField: 'record_scope', options: [
    { value: 'company', label: 'Company' },
    { value: 'user', label: 'Personal' },
  ] },
  // RETR Recruiting Intelligence filters
  { id: 'retrLoanCount', label: 'RETR Loan Count', type: 'number', dbField: 'retr_loan_count' },
  { id: 'retrLoanVolume', label: 'RETR Loan Volume', type: 'number', dbField: 'retr_loan_volume' },
  { id: 'retrTotalLoanCount', label: 'RETR Total Loan Count', type: 'number', dbField: 'retr_total_loan_count' },
  { id: 'retrTotalLoanVolume', label: 'RETR Total Volume', type: 'number', dbField: 'retr_total_loan_volume' },
  { id: 'retrAvgLoan', label: 'RETR Avg Loan', type: 'number', dbField: 'retr_avg_loan' },
  { id: 'retrLoType', label: 'RETR LO Type', type: 'text', dbField: 'retr_lo_type' },
  { id: 'retrState', label: 'RETR State', type: 'text', dbField: 'retr_state' },
  { id: 'retrNmlsId', label: 'NMLS ID', type: 'text', dbField: 'retr_nmls_id' },
  { id: 'retrYearsExperience', label: 'Years Experience', type: 'number', dbField: 'retr_years_experience' },
  { id: 'retrBuyerAgentsCount', label: 'Buyer Agents', type: 'number', dbField: 'retr_buyer_agents_count' },
  { id: 'retrCompanyName', label: 'RETR Company', type: 'text', dbField: 'retr_company_name' },
];

